import { useMutation, useQuery } from '@apollo/client'
import { getOperationName } from '@apollo/client/utilities'
import { Divider, Modal, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { ExternalLink } from 'lucide-react'

import { useAuth } from 'src/auth'
import AdminOnlyDot from 'src/components/AdminContainer/AdminOnlyDot'
import DNButton from 'src/components/Buttons/Button/DNButton'
import { CollapsibleSection } from 'src/components/CollapsibleSection'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_DRIVER_IQ_POLL_DEBUG_URL,
  GET_PACKAGE_ORDERS,
  REVIVE_DRIVER_IQ_ORDER_POLL,
} from 'src/graphql/packageOrders.graphql'
import { isAdmin } from 'src/lib/auth/permissions'

type PackageOrder = {
  packageOrderId?: string
  orderType?: string
  status?: string
}

type Props = {
  packageOrder?: PackageOrder
}

/**
 * The role check here only avoids a pointless request; the resolver is the
 * gate that actually enforces it.
 */
const AdminInngestSection = ({ packageOrder }: Props) => {
  const { currentUser } = useAuth()
  const admin = isAdmin(currentUser)

  const packageOrderId = packageOrder?.packageOrderId
  const isDriverIQ = packageOrder?.orderType === 'DRIVER_IQ'
  const hasFailed = packageOrder?.status === 'FAILED'

  const [confirmOpened, { open: openConfirm, close: closeConfirm }] =
    useDisclosure(false)

  const { data } = useQuery(GET_DRIVER_IQ_POLL_DEBUG_URL, {
    variables: { packageOrderId },
    skip: !admin || !isDriverIQ || !packageOrderId,
  })

  const [reviveOrderPoll, { loading: reviving }] = useMutation(
    REVIVE_DRIVER_IQ_ORDER_POLL,
    {
      refetchQueries: [getOperationName(GET_PACKAGE_ORDERS)],
      onCompleted: () => {
        closeConfirm()
        toast('Poll chain revived', 'success')
      },
      onError: (error) => {
        closeConfirm()
        toast(error.message || 'Failed to revive the poll chain', 'error')
      },
    }
  )

  const debugUrl = data?.driverIQPollDebugUrl

  if (!debugUrl) {
    return null
  }

  return (
    <>
      <Divider variant="dashed" />
      <CollapsibleSection
        title={
          <span className="flex flex-row items-center gap-1.5">
            INNGEST
            <AdminOnlyDot />
          </span>
        }
      >
        <div className="flex flex-col items-start gap-3">
          <a
            href={debugUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-row items-center gap-1 text-xs font-light text-doubleNickel-brand-500 hover:underline"
          >
            View poll runs in Inngest
            <ExternalLink size={10} strokeWidth={1} />
          </a>

          {hasFailed && (
            <DNButton variant="secondary" onClick={openConfirm}>
              Revive poll chain
            </DNButton>
          )}
        </div>

        <Modal
          opened={confirmOpened}
          onClose={closeConfirm}
          title="Revive poll chain"
          centered
        >
          <div className="flex flex-col gap-4">
            <Text size="sm" fw={300}>
              This resets the order to Pending and starts a fresh polling chain
              from attempt 1. Only do this if the chain died and the order is
              still expected to complete.
            </Text>
            <div className="flex flex-row justify-end gap-2">
              <DNButton variant="outline" onClick={closeConfirm}>
                Cancel
              </DNButton>
              <DNButton
                variant="primary"
                loading={reviving}
                onClick={() =>
                  reviveOrderPoll({ variables: { packageOrderId } })
                }
              >
                Confirm
              </DNButton>
            </div>
          </div>
        </Modal>
      </CollapsibleSection>
    </>
  )
}

export default AdminInngestSection
